import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import noop from 'utils/noop';

import { getInAppContent, previewTemplate } from 'modules/templatePreview/actions';
import { MessageMedium } from 'constants/messageMediums';
import {
  ANDROID,
  getActivePushDeviceWrapperById,
  TemplatePreview as TemplatePreviewComponent,
} from 'modules/templatePreview';
import { RefreshButton } from 'ui/button';
import { BUTTON_VARIANTS } from 'ui/button/constants';

import { EditContentButton } from './EditContentButton';
import { displayContent } from './TemplatePreview.constants';
import { ButtonContainer, PreviewContainer, StyledLink } from './TemplatePreview.styled';

export interface TemplatePreviewNonAuroraProps {
  /** content is just a passed in object with strings
   *  representing all of the text on the page.
   *  this makes Justin Tulk happy because it's prep work
   *  for a possible i18n future.
   */
  content?: typeof displayContent;
  /** string representation of which type of templates to display */
  messageMedium: MessageMedium;
  /** The id of the template for which we want to display the preview */
  templateId: number;
  /** The campaign of the template being viewed */
  campaignId?: number;
  /** callback that fires when clicking the attach template link */
  onAttachClick?: () => void;
  /** refetches the id for the template from the campaign id, useful if the user changes the template assocaiated with the campaign elsewhere */
  onRefreshClick?: () => void;
}

export const TemplatePreviewNonAurora: FC<
  React.PropsWithChildren<TemplatePreviewNonAuroraProps>
> = ({
  content = displayContent,
  messageMedium = MessageMedium.Email,
  templateId,
  campaignId,
  onAttachClick = noop,
  onRefreshClick = noop,
}): JSX.Element => {
  const dispatch = useDispatch();

  const activePushDeviceWrapper = useSelector((state) =>
    getActivePushDeviceWrapperById(state, { templateId }),
  );

  // since the template may be edited on a different page
  // we may be showing a stale preview if a change has occured.
  // thus we offer a way to refresh the preview
  const refreshPreview = () => {
    if (templateId) {
      // here we call the action/saga/reducer from /modules/previewTemplate to fetch a new version of the preview
      dispatch(
        previewTemplate({
          templateId,
          messageMedium,
          showAndroid: activePushDeviceWrapper === ANDROID, // for push messages,
          showExpanded: false, // for push messages
          showLighthouseVersion: true, // for in-app, sms, push messages
        }),
      );

      // if we are displaying an in-app message, we also need to refresh possible inbox data and
      // possible inAppDisplay information (background layer, opacity of that layer, location of the message on the phone...)
      if (messageMedium === MessageMedium.InApp) {
        dispatch(getInAppContent(templateId));
      }
    }
  };

  const handleRefreshClick = () => {
    onRefreshClick();
    refreshPreview();
  };

  // we want to refresh the template preview every time the editor is
  // re-opened to reflect the updated template name and content
  // in the event that a template change has been saved
  useEffect(() => {
    refreshPreview();
  }, [templateId]);

  return (
    <>
      <PreviewContainer data-test="send-node-template-preview">
        <ButtonContainer>
          <RefreshButton onClick={handleRefreshClick} variant={BUTTON_VARIANTS.SECONDARY} />
        </ButtonContainer>

        <TemplatePreviewComponent
          messageMedium={messageMedium}
          showInAppToggleButtons={true}
          showPushToggleButtons={true}
          templateId={templateId as number}
        />
      </PreviewContainer>
      {/*
      Show edit template button if a campaign has been created already.
      Otherwise, we would be accidentally allowing modifications to the base template.
      */}
      {campaignId && (
        <a target="_blank" href={`/templates/edit?templateId=${templateId}`}>
          <EditContentButton />
        </a>
      )}
      <StyledLink onClick={onAttachClick}>
        {content?.changeText || displayContent.changeText}
      </StyledLink>
    </>
  );
};
